import type { Purchase } from '~/types';
import api from '~/util/api';

export type PurchaseStatus = 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED';

export type PurchaseItem = {
  id: string;
  external_id: string;
  payment_link_url: string;
  user_id: string;
  course_id: string;
  coupon_id: string | null;
  price: number;
  payment_status: PurchaseStatus;
  is_manually_unlocked: boolean;
  original_expires_at: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  course: {
    id: string;
    title: string;
    description?: string;
  };
};

export type GetPurchasesResponse = {
  data: PurchaseItem[];
  total_items: number;
};

const PurchaseService = {
  getPurchases: async () => {
    const response = await api.get<GetPurchasesResponse>('/purchase');

    return response.data;
  },
  createPurchase: async (course_ids: string[], coupon_code?: string) => {
    const response = await api.post<{ message: string; paymentLink: { url: string } }>(
      '/purchase',
      { course_ids, ...(coupon_code?.trim() && { coupon_code: coupon_code.trim() }) },
    );

    return response.data;
  },

  cancelPurchase: async (
    purchaseId: string,
    payload: { refund_amount_cents: number; comments: string },
  ): Promise<void> => {
    await api.patch(`/purchase/${purchaseId}/cancel`, payload);
  },

  deletePurchase: async (purchaseId: string): Promise<void> => {
    await api.delete(`/purchase/${purchaseId}`);
  },

  updatePurchaseExpiration: async (purchaseId: string, newExpiresAt: string): Promise<void> => {
    await api.patch(`/purchase/${purchaseId}/expiration`, {
      new_expires_at: newExpiresAt,
    });
  },

  manualUnlock: async (params: {
    userId: string;
    courseId: string;
    newExpiresAt: string;
    paidAmountCents?: number;
    comments?: string;
  }) => {
    const { userId, courseId, newExpiresAt, paidAmountCents, comments } = params;
    const response = await api.post<{ data: Purchase }>('/purchase/manual-unlock', {
      user_id: userId,
      course_id: courseId,
      new_expires_at: newExpiresAt,
      ...(typeof paidAmountCents === 'number' ? { paid_amount_cents: paidAmountCents } : {}),
      ...(typeof comments === 'string' && comments.trim() ? { comments: comments.trim() } : {}),
    });

    return response.data;
  },
};

export default PurchaseService;
